"use client";

import Link from "next/link";
import { forwardRef, type ReactNode, memo } from "react";

import { NavigationMenuLink } from "@/components/navbar/navigation-menu";
import { cn } from "@/lib/utils";

export interface ListItemProps {
  href: string;
  title: string;
  children: ReactNode;
  className?: string;
}

/**
 * Generic list tile (text-only).
 *
 * - Uses <Link> for prefetching.
 * - `memo` + `forwardRef` to avoid re-renders and keep type inference.
 */
export const ListItem = memo(
  forwardRef<HTMLAnchorElement, ListItemProps>(function ListItem(
    { href, title, children, className },
    ref,
  ) {
    return (
      <li>
        <NavigationMenuLink asChild>
          <Link
            ref={ref}
            href={href}
            className={cn(
              "block select-none space-y-1 rounded-md p-3 leading-none outline-none transition-colors hover:bg-accent/80 focus:bg-accent/90",
              className,
            )}
          >
            <span className="text-p2 font-semibold">{title}</span>
            <p className="text-p2 text-muted-foreground line-clamp-2">
              {children}
            </p>
          </Link>
        </NavigationMenuLink>
      </li>
    );
  }),
);
